import { I18n } from "@assets/strings";
import React, { JSX, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import { useServices } from "@core/servicesContext";
import { Media } from "@domain/media/media";
import { handleUploadFileError } from "@utils/file";
import { createToast } from "@ui/toasts/utils";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { FileInput } from "@ui/components/inputs/fileInput";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { Stack } from "@ui/components/layout";

const MAX_SIZE_ALLOWED = 200_000_000; // 200MB
const ASSET_MANAGER_MAX_SIZE_ALLOWED = 250_000_000; // 250MB;

const validFormats = ["image/png", "image/jpg", "image/jpeg", "image/gif", "image/webp"];
interface FileUploadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
	type?: "file" | "image" | "pdf" | "video" | "document";
	onUploadStart?: (taskId: string) => void;
	onUpload?: (media: Media, fileName: string, taskId: string) => void;
	onError?: () => void;
	rename?: boolean;
	onMultipleUpload?: (medias: Media[]) => void;
	multiple?: boolean;
	onUploadProgress?: (progress: number) => void;
	onErrorChange?: (message: string) => void;
	showInlineError?: boolean;
	children: (dragging: boolean, loading: boolean, progress: number) => JSX.Element;
	bigSize?: boolean;
}
export const FileUpload: React.FC<FileUploadProps> = ({
	type = "image",
	onUploadStart,
	onUpload,
	onError,
	rename,
	onMultipleUpload,
	multiple,
	onUploadProgress,
	onErrorChange,
	showInlineError = true,
	children,
	bigSize,
	...htmlProps
}) => {
	const { mediaService } = useServices();
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState("");
	const [progress, setProgress] = useState(0);
	const [renamedFile, setRenamedFile] = useState<File>();
	const [fileName, setFileName] = useState("");

	const reportError = (message: string): void => {
		setError(message);
		onErrorChange?.(message);
	};

	const validateFiles = (files: File[]): boolean => {
		for (const file of files) {
			const fileType = file.type;
			const biggerThanAllowed = file.size > ASSET_MANAGER_MAX_SIZE_ALLOWED;
			const wrongFormat =
				(type === "image" && !validFormats.includes(fileType)) ||
				(type === "pdf" && fileType !== "application/pdf") ||
				(type === "video" && !fileType.startsWith("video/")) ||
				(type === "document" && !validFormats.includes(fileType) && fileType !== "application/pdf");
			if (wrongFormat) {
				reportError(I18n.t("upload.error.format"));
				return false;
			}
			if (type === "video") {
				if (biggerThanAllowed) {
					reportError(I18n.t("upload.error.size"));
					return false;
				}
			} else if (file.size > MAX_SIZE_ALLOWED && !bigSize) {
				createToast(I18n.t("upload.error.size_small.title"), I18n.t("upload.error.size_small.message"), "error");
				return false;
			} else if (bigSize && biggerThanAllowed) {
				reportError(I18n.t("upload.error.size"));
				return false;
			}
		}
		return true;
	};

	// const handleError = (e: Error): void => {
	// 	if (e.message === UploadError.ALREADY_PENDING) {
	// 		createToast(I18n.t("error"), I18n.t("upload.error.pending"), "error");
	// 	}
	// 	if (e.message === UploadError.UNKNOWN) {
	// 		createToast(I18n.t("error"), I18n.t("upload.erorr.unknown"), "error");
	// 	}
	// 	if (e.message === UploadError.CANCELED) {
	// 		createToast(I18n.t("info"), I18n.t("upload.canceled"));
	// 	}
	// 	onError?.();
	// };

	return (
		<div {...htmlProps}>
			{renamedFile ? (
				<Stack gap={12} align="stretch">
					<PrimaryMedium>{I18n.t("workspace.assets.name.label")}</PrimaryMedium>
					<SimpleTextInput value={fileName} onChange={setFileName} />
					{showInlineError && error && <Error>{error}</Error>}
					<PrimaryButton
						onClick={async () => {
							const taskId = uuid();
							try {
								setProgress(0);
								onUploadProgress?.(0);
								onUploadStart?.(taskId);
								setLoading(true);
								const media = await mediaService.uploadMedia(new File([renamedFile], fileName, renamedFile), (p) => {
									setProgress(p);
									onUploadProgress?.(p);
								});
								onUpload?.(media, fileName, taskId);
							} catch (e) {
								handleUploadFileError(e as Error);
							} finally {
								setLoading(false);
							}
						}}
						disabled={!fileName}
					>
						{I18n.t("add")}
					</PrimaryButton>
				</Stack>
			) : (
				<>
					{showInlineError && error && <Error>{error}</Error>}
					<FileInput
						disabled={loading}
						type={type}
						multiple={!!onMultipleUpload || multiple}
						onChange={async (files) => {
							if (files) {
								if (!validateFiles(files)) {
									return;
								}
								try {
									const medias: Media[] = [];
									if (!rename) {
										const taskId = uuid();
										onUploadStart?.(taskId);
										setProgress(0);
										onUploadProgress?.(0);
										setLoading(true);
										for (const file of files) {
											const media = await mediaService.uploadMedia(file, (p) => {
												setProgress(p);
												onUploadProgress?.(p);
											});
											medias.push(media);
										}
										if (onMultipleUpload) {
											onMultipleUpload(medias);
										} else if (onUpload) {
											onUpload(medias[0], files[0].name, taskId);
										}
									} else {
										setRenamedFile(files[0]);
										setFileName(files[0].name);
									}
									reportError("");
								} catch (e) {
									handleUploadFileError(e as Error);
								} finally {
									setLoading(false);
								}
							}
						}}
					>
						{(dragging) => children(dragging, loading, progress)}
					</FileInput>
				</>
			)}
		</div>
	);
};

const Error = styled.div`
	margin-bottom: 10px;
	font-size: 0.875rem;
	color: ${({ theme }) => theme.colors.red};
`;
